import loadable from '@loadable/component';

import { resourceTypes, analyticsNames } from '@johnlewispartnership/wtr-content-component-library';
import {
  RECIPE_CARDS_GRID,
  RECIPE_SEARCHBAR,
} from '@johnlewispartnership/wtr-content-component-library/dist/component-library/constants/cmsResourceTypes';
import RecipeSearchBar from './components/recipes/RecipeSearchBar';
import RecipeListingPage from './components/recipes/RecipeListingPage';

const {
  ACCORDION,
  CALLTOACTION_BUTTON,
  CALLTOACTION_SLIDE,
  CARD_CONTAINER,
  CLICK_TO_BUY,
  COLUMN_LAYOUT,
  COMPETITIONS_FORM,
  CONTACT_US_CONTENT_FRAGMENT,
  CONTAINER,
  CONTENT_FRAGMENT,
  DIVIDER,
  EDITABLE_RESPONSIVE_GRID,
  EXPERIENCE_FRAGMENT_EDITABLE_RESPONSIVE_GRID,
  EXPERIENCE_FRAGMENT_REFERENCE,
  EXTENDED_NAVIGATION_CARD,
  EXTENDED_NAVIGATION_ROW,
  FULLWIDTH_NAVIGATION,
  GENERIC_NAVIGATION,
  HORIZONTAL_LINE,
  IMAGE,
  NAV_LINKS,
  PAGE_TITLE,
  RESPONSIVE_GRID,
  RICH_TEXT,
  SECTION_TITLE,
  SHOP_WINDOWS,
  VIDEO,
  VISUAL_NAVIGATION_ROW,
  VISUAL_NAVIGATION,
} = resourceTypes;

const {
  CALLTOACTION_BUTTON_NAME,
  CLICK_TO_BUY_NAME,
  CONTACT_US_NAME,
  CONTAINER_NAME,
  CONTENT_FRAGMENT_NAME,
  EXTENDED_NAVIGATION_CARD_NAME,
  EXTENDED_NAVIGATION_ROW_NAME,
  FULLWIDTH_NAVIGATION_NAME,
  GENERIC_NAVIGATION_NAME,
  HORIZONTAL_LINE_NAME,
  IMAGE_NAME,
  RICH_TEXT_NAME,
  SHOP_WINDOWS_NAME,
  VIDEO_NAME,
  VISUAL_NAVIGATION_NAME,
} = analyticsNames;

const Accordion = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/Accordion'),
);
const CallToActionButton = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/CallToActionButton'),
);
const CarouselSlide = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/CarouselSlide'),
);
const ClickToBuy = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/ClickToBuy'),
);
const ColumnLayout = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/ColumnLayout'),
);
const CompetitionsFormWrapper = loadable(() => import('./components/CompetitionsFormWrapper'));
const Container = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/Container'),
);
const ContactUsCard = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/ContactUsCard'),
);
const ContentFragment = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/ContentFragment'),
);
const Divider = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/Divider'),
);
const ExtendedNavigationCard = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/ExtendedNavigationCard'),
);
const FullWidthNavigation = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/FullWidthNavigation'),
);
const CategoryLinks = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/CategoryLinks'),
);
const FullWidthRow = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/FullWidthRow'),
);
const HorizontalLine = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/HorizontalLine'),
);
const Image = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/Image'),
);
const NavLinks = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/NavLinks'),
);
const PageTitle = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/PageTitle'),
);
const ResponsiveGrid = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/ResponsiveGrid'),
);
const RichText = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/RichText'),
);
const SectionTitle = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/SectionTitle'),
);
const ShopWindows = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/ShopWindows'),
);
const Video = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/Video'),
);
const VisualNavigation = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/VisualNavigation'),
);

const CardContainer = loadable(
  () =>
    import('@johnlewispartnership/wtr-content-component-library/dist/component-library/components/CardContainer'),
);

export type CmsComponentMapProps = {
  component: any;
  // A name used for when analytics data-attributes should be added to the component
  analyticsName?: string;
};

const cmsComponentMap = new Map<resourceTypes.CmsResourceTypes, CmsComponentMapProps>([
  [ACCORDION, { component: Accordion }],
  [COLUMN_LAYOUT, { component: ColumnLayout }],
  [CALLTOACTION_BUTTON, { component: CallToActionButton, analyticsName: CALLTOACTION_BUTTON_NAME }],
  [CALLTOACTION_SLIDE, { component: CarouselSlide }],
  [CLICK_TO_BUY, { component: ClickToBuy, analyticsName: CLICK_TO_BUY_NAME }],
  [COMPETITIONS_FORM, { component: CompetitionsFormWrapper }],
  [CONTACT_US_CONTENT_FRAGMENT, { component: ContactUsCard, analyticsName: CONTACT_US_NAME }],
  [CONTAINER, { component: Container, analyticsName: CONTAINER_NAME }],
  [CONTENT_FRAGMENT, { component: ContentFragment, analyticsName: CONTENT_FRAGMENT_NAME }],
  [DIVIDER, { component: Divider }],
  [
    EXTENDED_NAVIGATION_CARD,
    { component: ExtendedNavigationCard, analyticsName: EXTENDED_NAVIGATION_CARD_NAME },
  ],
  [
    EXTENDED_NAVIGATION_ROW,
    { component: FullWidthRow, analyticsName: EXTENDED_NAVIGATION_ROW_NAME },
  ],
  [
    FULLWIDTH_NAVIGATION,
    { component: FullWidthNavigation, analyticsName: FULLWIDTH_NAVIGATION_NAME },
  ],
  [GENERIC_NAVIGATION, { component: CategoryLinks, analyticsName: GENERIC_NAVIGATION_NAME }],
  [HORIZONTAL_LINE, { component: HorizontalLine, analyticsName: HORIZONTAL_LINE_NAME }],
  [IMAGE, { component: Image, analyticsName: IMAGE_NAME }],
  [NAV_LINKS, { component: NavLinks }],
  [PAGE_TITLE, { component: PageTitle }],
  [RESPONSIVE_GRID, { component: ResponsiveGrid }],
  [RICH_TEXT, { component: RichText, analyticsName: RICH_TEXT_NAME }],
  [SECTION_TITLE, { component: SectionTitle }],
  [SHOP_WINDOWS, { component: ShopWindows, analyticsName: SHOP_WINDOWS_NAME }],
  [VIDEO, { component: Video, analyticsName: VIDEO_NAME }],
  [VISUAL_NAVIGATION, { component: VisualNavigation, analyticsName: VISUAL_NAVIGATION_NAME }],
  [VISUAL_NAVIGATION_ROW, { component: FullWidthRow, analyticsName: EXTENDED_NAVIGATION_ROW_NAME }],
  [CARD_CONTAINER, { component: CardContainer }],
  [EXPERIENCE_FRAGMENT_REFERENCE, { component: ResponsiveGrid }],
  [EXPERIENCE_FRAGMENT_EDITABLE_RESPONSIVE_GRID, { component: ResponsiveGrid }],
  [EDITABLE_RESPONSIVE_GRID, { component: ResponsiveGrid }],
  [RECIPE_CARDS_GRID, { component: RecipeListingPage }],
  [RECIPE_SEARCHBAR, { component: RecipeSearchBar }],
]);

export default cmsComponentMap;
